import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { money, num, pct } from "@/lib/format";
import { CARRIER_LOGO_COLOR, STATUS_COLORS } from "@/lib/constants";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
  AreaChart, Area, CartesianGrid,
} from "recharts";
import {
  BarChart3, AlertTriangle, TrendingUp, TrendingDown, Minus, Trophy,
  ArrowUpRight, ShieldAlert, Mail, CheckCircle2, Star,
} from "lucide-react";
import Leaderboard from "@/components/motivation/Leaderboard";

const COLORS = ["#007AFF", "#22C55E", "#F59E0B", "#A855F7", "#FF3B30", "#0EA5E9"];
const PERIODS = [["7", "7D"], ["30", "30D"], ["90", "90D"], ["365", "1Y"], ["all", "All"]];

const axisStyle = { fontSize: 11, fill: "hsl(var(--muted-foreground))" };
const tooltipStyle = {
  background: "hsl(var(--card))", border: "1px solid hsl(var(--border))",
  borderRadius: 6, fontSize: 12, color: "hsl(var(--foreground))",
};

function Delta({ kpi, point = false, invert = false }) {
  const d = point ? kpi.delta : kpi.delta_pct;
  if (d == null) return <span className="text-[11px] text-muted-foreground">vs prior · no data</span>;
  const flat = Math.abs(d) < 0.05;
  const good = invert ? d < 0 : d > 0;
  const Icon = flat ? Minus : d > 0 ? TrendingUp : TrendingDown;
  const color = flat
    ? "text-muted-foreground"
    : good
    ? "text-[hsl(var(--success))]"
    : "text-[hsl(var(--destructive))]";
  const label = point ? `${d > 0 ? "+" : ""}${d.toFixed(1)} pts` : `${d > 0 ? "+" : ""}${d.toFixed(1)}%`;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${color}`}>
      <Icon className="h-3 w-3" />{label}<span className="font-normal text-muted-foreground">vs prior</span>
    </span>
  );
}

function Kpi({ label, value, kpi, point = false, invert = false, accent }) {
  return (
    <div className="group border border-border bg-card p-4 transition-colors hover:bg-secondary/40" data-testid={`kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="label-caps text-muted-foreground">{label}</div>
      <div className={`metric-num mt-2 text-2xl font-bold leading-none ${accent || ""}`}>{value}</div>
      <div className="mt-2">{kpi ? <Delta kpi={kpi} point={point} invert={invert} /> : null}</div>
    </div>
  );
}

function Panel({ title, action, children, className = "" }) {
  return (
    <div className={`flex flex-col border border-border bg-card ${className}`}>
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <span className="label-caps">{title}</span>
        {action}
      </div>
      <div className="flex-1 p-4">{children}</div>
    </div>
  );
}

function Empty({ children }) {
  return <p className="flex h-full min-h-[180px] items-center justify-center text-center text-sm text-muted-foreground">{children}</p>;
}

function fmtHours(h) {
  if (h == null) return "—";
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 48) return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
}

function FollowUpPanel({ navigate }) {
  const [fu, setFu] = useState(null);
  useEffect(() => {
    api.get("/analytics/follow-ups").then((r) => setFu(r.data)).catch(() => {});
  }, []);
  if (!fu || fu.delivered_count === 0) return null;
  const stats = [
    ["Delivered Loads", num(fu.delivered_count), ""],
    ["Followed Up", num(fu.followed_up_count), ""],
    ["Follow-up Rate", pct(fu.follow_up_rate), fu.follow_up_rate >= 75 ? "text-[hsl(var(--success))]" : fu.follow_up_rate < 40 ? "text-[hsl(var(--destructive))]" : "text-[hsl(var(--warning))]"],
    ["Avg Response", fmtHours(fu.avg_response_hours), ""],
    ["Repeat Quotes", `${num(fu.repeat_count)} · ${pct(fu.repeat_rate)}`, "text-[hsl(var(--success))]"],
  ];
  return (
    <Panel title="Follow-up Engine · Repeat Business" action={<Mail className="h-4 w-4 text-[hsl(var(--success))]" />} className="lg:col-span-3">
      <div data-testid="followup-panel" className="space-y-4">
        <div className="grid grid-cols-2 gap-px bg-border sm:grid-cols-5">
          {stats.map(([l, v, c]) => (
            <div key={l} className="bg-card px-3 py-2.5" data-testid={`followup-stat-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="label-caps text-muted-foreground">{l}</div>
              <div className={`metric-num mt-1 text-lg font-bold leading-none ${c}`}>{v}</div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div>
            <div className="label-caps mb-2 text-muted-foreground">Rep follow-up speed</div>
            {fu.reps.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">No follow-ups sent yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="label-caps text-left text-muted-foreground">
                    <th className="pb-1.5 pr-4 font-semibold">Rep</th>
                    <th className="pb-1.5 pr-4 text-right font-semibold">Sent</th>
                    <th className="pb-1.5 pr-4 text-right font-semibold">Avg Speed</th>
                    <th className="pb-1.5 text-right font-semibold">Repeat</th>
                  </tr>
                </thead>
                <tbody>
                  {fu.reps.map((r, i) => (
                    <tr key={r.rep} className="border-t border-border" data-testid={`followup-rep-row-${i}`}>
                      <td className="py-1.5 pr-4 font-medium">{i === 0 && <Trophy className="mr-1 inline h-3.5 w-3.5 text-[hsl(var(--warning))]" />}{r.rep}</td>
                      <td className="py-1.5 pr-4 text-right tabular-nums">{num(r.sent)}</td>
                      <td className="py-1.5 pr-4 text-right tabular-nums">{fmtHours(r.avg_response_hours)}</td>
                      <td className="py-1.5 text-right tabular-nums text-[hsl(var(--success))]">{num(r.repeat)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div>
            <div className="label-caps mb-2 text-muted-foreground">Awaiting follow-up</div>
            {fu.pending.length === 0 ? (
              <p className="flex items-center justify-center gap-1.5 py-6 text-sm text-[hsl(var(--success))]">
                <CheckCircle2 className="h-4 w-4" /> Every delivered load has been followed up. 👏
              </p>
            ) : (
              <div className="space-y-1.5">
                {fu.pending.map((q) => (
                  <button key={q.id} data-testid={`followup-pending-${q.quote_number}`} onClick={() => navigate("/shipments")}
                    className="flex w-full items-center justify-between rounded-md border border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/5 px-3 py-2 text-left text-sm transition-colors hover:bg-[hsl(var(--success))]/10">
                    <span className="flex items-center gap-2 truncate">
                      <Mail className="h-4 w-4 shrink-0 text-[hsl(var(--success))]" />
                      <span className="font-semibold">{q.quote_number}</span>
                      <span className="truncate text-muted-foreground">{q.customer_name}</span>
                    </span>
                    <span className="whitespace-nowrap text-xs text-muted-foreground">delivered {q.delivered_at ? String(q.delivered_at).slice(0, 10) : ""}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Panel>
  );
}

function Stars({ value, size = "text-sm" }) {
  return (
    <span className={`inline-flex ${size}`} aria-label={`${value} out of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`h-3.5 w-3.5 ${n <= Math.round(value) ? "fill-[#F5A623] text-[#F5A623]" : "text-muted-foreground/40"}`} />
      ))}
    </span>
  );
}

function SatisfactionPanel() {
  const [cs, setCs] = useState(null);
  useEffect(() => {
    api.get("/analytics/satisfaction").then((r) => setCs(r.data)).catch(() => {});
  }, []);
  if (!cs || cs.rated_count === 0) return null;
  const maxDist = Math.max(...Object.values(cs.distribution), 1);
  return (
    <Panel title="Customer Satisfaction · by Carrier" action={<Star className="h-4 w-4 fill-[#F5A623] text-[#F5A623]" />} className="lg:col-span-3">
      <div data-testid="satisfaction-panel" className="space-y-4">
        <div className="grid grid-cols-2 gap-px bg-border sm:grid-cols-4">
          {[
            ["Avg Rating", cs.avg_stars != null ? `${cs.avg_stars.toFixed(2)} ★` : "—", "text-[#F5A623]"],
            ["Ratings", num(cs.rated_count), ""],
            ["Delivered", num(cs.delivered_count), ""],
            ["Response Rate", pct(cs.response_rate), cs.response_rate >= 30 ? "text-[hsl(var(--success))]" : "text-[hsl(var(--warning))]"],
          ].map(([l, v, c]) => (
            <div key={l} className="bg-card px-3 py-2.5" data-testid={`csat-stat-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="label-caps text-muted-foreground">{l}</div>
              <div className={`metric-num mt-1 text-lg font-bold leading-none ${c}`}>{v}</div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div>
            <div className="label-caps mb-2 text-muted-foreground">Rating by carrier</div>
            <table className="w-full text-sm">
              <thead>
                <tr className="label-caps text-left text-muted-foreground">
                  <th className="pb-1.5 pr-4 font-semibold">Carrier</th>
                  <th className="pb-1.5 pr-4 text-right font-semibold">Avg</th>
                  <th className="pb-1.5 pr-4 font-semibold">Rating</th>
                  <th className="pb-1.5 text-right font-semibold">#</th>
                </tr>
              </thead>
              <tbody>
                {cs.by_carrier.map((r, i) => (
                  <tr key={r.carrier} className="border-t border-border" data-testid={`csat-carrier-row-${i}`}>
                    <td className="py-1.5 pr-4 font-medium">{r.carrier}</td>
                    <td className="py-1.5 pr-4 text-right tabular-nums font-semibold">{r.avg.toFixed(2)}</td>
                    <td className="py-1.5 pr-4"><Stars value={r.avg} /></td>
                    <td className="py-1.5 text-right tabular-nums text-muted-foreground">{num(r.count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-3 space-y-1">
              {[5, 4, 3, 2, 1].map((n) => (
                <div key={n} className="flex items-center gap-2 text-xs">
                  <span className="w-6 tabular-nums text-muted-foreground">{n}★</span>
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-secondary">
                    <div className="h-full rounded-full bg-[#F5A623]" style={{ width: `${(cs.distribution[n] / maxDist) * 100}%` }} />
                  </div>
                  <span className="w-6 text-right tabular-nums text-muted-foreground">{cs.distribution[n]}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="label-caps mb-2 text-muted-foreground">Recent comments</div>
            {cs.comments.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">No written feedback yet.</p>
            ) : (
              <div className="space-y-2">
                {cs.comments.map((c, i) => (
                  <div key={i} className="rounded-md border border-border bg-card px-3 py-2" data-testid={`csat-comment-${i}`}>
                    <div className="flex items-center justify-between gap-2">
                      <Stars value={c.stars} />
                      <span className="truncate text-xs text-muted-foreground">{c.customer} · {c.carrier}</span>
                    </div>
                    <p className="mt-1 text-sm text-foreground">“{c.comment}”</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Panel>
  );
}

export default function Analytics() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("30");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    api.get(`/analytics/overview?period=${period}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [period]);

  const header = (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1 className="font-heading text-3xl font-black tracking-tighter">Insights & Analytics</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {data ? `${num(data.total_quotes)} quotes in window · ${num(data.all_time_quotes)} all-time` : "Loading…"}
        </p>
      </div>
      <div className="inline-flex overflow-hidden rounded-md border border-border bg-card" data-testid="period-switcher">
        {PERIODS.map(([val, label]) => (
          <button
            key={val}
            data-testid={`period-${val}`}
            onClick={() => setPeriod(val)}
            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
              period === val ? "bg-[hsl(var(--primary))] text-white" : "text-muted-foreground hover:bg-secondary"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );

  if (!data) return <div className="space-y-5 animate-fade-up">{header}<div className="text-muted-foreground">Loading insights…</div></div>;

  if (data.all_time_quotes === 0) {
    return (
      <div className="space-y-5 animate-fade-up">
        {header}
        <div className="flex h-64 flex-col items-center justify-center border border-dashed border-border bg-card text-muted-foreground">
          <BarChart3 className="mb-2 h-8 w-8 opacity-40" />Save some quotes to unlock analytics.
        </div>
      </div>
    );
  }

  const k = data.kpis;
  const winData = [
    { name: "Won", value: data.won_count },
    { name: "Lost", value: data.lost_count },
  ];

  return (
    <div className={`space-y-5 animate-fade-up ${loading ? "opacity-60 transition-opacity" : ""}`}>
      {header}

      {/* KPI strip */}
      <div className="grid grid-cols-2 gap-px bg-border lg:grid-cols-3 xl:grid-cols-6">
        <Kpi label="Quotes" value={num(k.total_quotes.value)} kpi={k.total_quotes} />
        <Kpi label="Quoted Value" value={money(k.quoted_value.value)} kpi={k.quoted_value} />
        <Kpi label="Won Value" value={money(k.won_value.value)} kpi={k.won_value} accent="text-[hsl(var(--success))]" />
        <Kpi label="Win Rate" value={pct(k.win_rate.value)} kpi={k.win_rate} point />
        <Kpi label="Avg Margin" value={pct(k.avg_margin_pct.value)} kpi={k.avg_margin_pct} point
          accent={k.avg_margin_pct.value < 15 ? "text-[hsl(var(--destructive))]" : ""} />
        <Kpi label="Avg Carrier Cost" value={money(k.avg_carrier_cost.value)} kpi={k.avg_carrier_cost} invert />
      </div>

      {/* Trend + win funnel */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <Panel title="Quoted Value & Volume" className="lg:col-span-2">
          {data.timeline.length === 0 ? <Empty>No quotes in this window.</Empty> : (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={data.timeline} margin={{ left: -10, right: 6 }}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#007AFF" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#007AFF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="date" tick={axisStyle} axisLine={false} tickLine={false}
                  tickFormatter={(d) => d.slice(5)} minTickGap={24} />
                <YAxis tick={axisStyle} axisLine={false} tickLine={false} tickFormatter={(v) => `$${num(v / 1000)}k`} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => n === "revenue" ? money(v) : num(v)} />
                <Area type="monotone" dataKey="revenue" stroke="#007AFF" strokeWidth={2} fill="url(#rev)" name="revenue" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </Panel>

        <Panel title="Win Rate">
          <div className="relative">
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={winData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                  innerRadius={55} outerRadius={80} paddingAngle={2} stroke="none">
                  <Cell fill="#22C55E" />
                  <Cell fill="#FF3B30" />
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
              </PieChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center pt-1">
              <span className="metric-num text-3xl font-bold">{pct(data.kpis.win_rate.value)}</span>
              <span className="label-caps text-muted-foreground">win rate</span>
            </div>
          </div>
          <div className="mt-2 grid grid-cols-3 gap-px bg-border text-center">
            {[["Open", data.open_count, ""], ["Won", data.won_count, "text-[hsl(var(--success))]"], ["Lost", data.lost_count, "text-[hsl(var(--destructive))]"]].map(([l, v, c]) => (
              <div key={l} className="bg-card py-2">
                <div className={`metric-num text-lg font-bold ${c}`}>{num(v)}</div>
                <div className="label-caps text-muted-foreground">{l}</div>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      {/* Team leaderboard */}
      <Leaderboard />

      {/* Delivery follow-up engine */}
      <FollowUpPanel navigate={navigate} />

      <SatisfactionPanel />

      {/* Pipeline + margin distribution + carrier mix */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <Panel title="Quote Pipeline">
          {data.pipeline.length === 0 ? <Empty>No quotes yet.</Empty> : (
            <div className="space-y-2">
              {data.pipeline.map((p) => {
                const max = Math.max(...data.pipeline.map((x) => x.count), 1);
                return (
                  <div key={p.status} data-testid={`pipeline-${p.status}`}>
                    <div className="mb-1 flex items-center justify-between text-xs">
                      <span className={`rounded border px-1.5 py-0.5 font-bold uppercase tracking-wider ${STATUS_COLORS[p.status] || ""}`}>{p.status}</span>
                      <span className="text-muted-foreground">{num(p.count)} · {money(p.value)}</span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded bg-secondary">
                      <div className="h-full rounded bg-[hsl(var(--primary))]" style={{ width: `${(p.count / max) * 100}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Panel>

        <Panel title="Margin Distribution">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.margin_distribution} margin={{ left: -16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="bucket" tick={{ ...axisStyle, fontSize: 9 }} axisLine={false} tickLine={false} interval={0} angle={-15} textAnchor="end" height={42} />
              <YAxis tick={axisStyle} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "hsl(var(--secondary))" }} />
              <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                {data.margin_distribution.map((b, i) => (
                  <Cell key={i} fill={i === 0 ? "#FF3B30" : i === 1 ? "#F59E0B" : "#22C55E"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Carrier Mix">
          {data.carrier_mix.length === 0 ? <Empty>No carrier selections yet.</Empty> : (
            <div className="space-y-3">
              {data.carrier_mix.map((c) => {
                const total = data.carrier_mix.reduce((s, x) => s + x.count, 0) || 1;
                const share = (c.count / total) * 100;
                return (
                  <div key={c.carrier_code}>
                    <div className="mb-1 flex items-center justify-between text-xs">
                      <span className="flex items-center gap-2 font-semibold">
                        <span className="h-2.5 w-2.5 rounded-sm" style={{ background: CARRIER_LOGO_COLOR[c.carrier_code] || "#71717A" }} />
                        {c.carrier}
                      </span>
                      <span className="text-muted-foreground">{num(c.count)} · avg {money(c.avg_cost)}</span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded bg-secondary">
                      <div className="h-full rounded" style={{ width: `${share}%`, background: CARRIER_LOGO_COLOR[c.carrier_code] || "#71717A" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Panel>
      </div>

      {/* Rep leaderboard */}
      <Panel title="Sales Rep Leaderboard" action={<Trophy className="h-4 w-4 text-[hsl(var(--warning))]" />}>
        {data.rep_leaderboard.length === 0 ? <Empty>No reps yet.</Empty> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="label-caps text-left text-muted-foreground">
                  <th className="pb-2 pr-4 font-semibold">Rep</th>
                  <th className="pb-2 pr-4 text-right font-semibold">Quotes</th>
                  <th className="pb-2 pr-4 text-right font-semibold">Shipments</th>
                  <th className="pb-2 pr-4 text-right font-semibold">Won</th>
                  <th className="pb-2 pr-4 text-right font-semibold">Avg Margin</th>
                  <th className="pb-2 text-right font-semibold">Quoted Value</th>
                </tr>
              </thead>
              <tbody>
                {data.rep_leaderboard.map((r, i) => (
                  <tr key={r.rep} className="border-t border-border" data-testid={`rep-row-${i}`}>
                    <td className="py-2 pr-4 font-medium">{i === 0 && <Trophy className="mr-1 inline h-3.5 w-3.5 text-[hsl(var(--warning))]" />}{r.rep}</td>
                    <td className="py-2 pr-4 text-right tabular-nums">{num(r.quotes)}</td>
                    <td className="py-2 pr-4 text-right font-semibold tabular-nums text-[hsl(var(--primary))]" data-testid={`rep-shipments-${i}`}>{num(r.shipments)}</td>
                    <td className="py-2 pr-4 text-right tabular-nums text-[hsl(var(--success))]">{num(r.won)}</td>
                    <td className={`py-2 pr-4 text-right tabular-nums ${r.avg_margin_pct < 15 ? "text-[hsl(var(--destructive))]" : ""}`}>{pct(r.avg_margin_pct)}</td>
                    <td className="py-2 text-right font-semibold tabular-nums">{money(r.total_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* Products + region + mode */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Panel title="Most Quoted Products">
          {data.most_quoted_products.length === 0 ? <Empty>No products yet.</Empty> : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.most_quoted_products} layout="vertical" margin={{ left: 10 }}>
                <XAxis type="number" tick={axisStyle} axisLine={false} tickLine={false} allowDecimals={false} />
                <YAxis type="category" dataKey="product" width={120} tick={axisStyle} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "hsl(var(--secondary))" }} />
                <Bar dataKey="count" fill="#007AFF" radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Panel>

        <Panel title="Avg Carrier Cost by Region">
          {data.avg_cost_by_region.length === 0 ? <Empty>No region data yet.</Empty> : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.avg_cost_by_region}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="region" tick={{ ...axisStyle, fontSize: 9 }} axisLine={false} tickLine={false} interval={0} angle={-15} textAnchor="end" height={42} />
                <YAxis tick={axisStyle} axisLine={false} tickLine={false} tickFormatter={(v) => `$${num(v)}`} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "hsl(var(--secondary))" }} formatter={(v) => money(v)} />
                <Bar dataKey="avg_cost" fill="#F59E0B" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Panel>
      </div>

      {/* Actionable lists */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Panel title="Lowest-Margin Quotes" action={<ShieldAlert className="h-4 w-4 text-[hsl(var(--destructive))]" />}>
          {data.low_margin_quotes.length === 0 ? (
            <Empty>No low-margin quotes. Healthy margins 👍</Empty>
          ) : (
            <div className="space-y-1.5">
              {data.low_margin_quotes.slice(0, 8).map((q) => (
                <button key={q.id} data-testid={`low-margin-${q.quote_number}`} onClick={() => navigate(`/quotes/${q.id}`)}
                  className="flex w-full items-center justify-between rounded-md border border-border bg-secondary/30 px-3 py-2 text-left text-sm transition-colors hover:border-[hsl(var(--destructive))]/40 hover:bg-[hsl(var(--destructive))]/5">
                  <span className="flex items-center gap-2 truncate">
                    <span className="font-semibold">{q.quote_number}</span>
                    <span className="truncate text-muted-foreground">{q.customer || "—"}</span>
                  </span>
                  <span className="flex items-center gap-2 whitespace-nowrap">
                    <span className="font-bold text-[hsl(var(--destructive))]">{pct(q.margin_pct)}</span>
                    <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground" />
                  </span>
                </button>
              ))}
            </div>
          )}
        </Panel>

        <Panel title="Quotes Requiring Review" action={<AlertTriangle className="h-4 w-4 text-[hsl(var(--warning))]" />}>
          {data.needs_review_quotes.length === 0 ? (
            <Empty>No quotes need review. 👍</Empty>
          ) : (
            <div className="space-y-1.5">
              {data.needs_review_quotes.slice(0, 8).map((q) => (
                <button key={q.id} data-testid={`needs-review-${q.quote_number}`} onClick={() => navigate(`/quotes/${q.id}`)}
                  className="flex w-full items-center justify-between rounded-md border border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/5 px-3 py-2 text-left text-sm transition-colors hover:bg-[hsl(var(--warning))]/10">
                  <span className="flex items-center gap-2 truncate">
                    <AlertTriangle className="h-4 w-4 shrink-0 text-[hsl(var(--warning))]" />
                    <span className="font-semibold">{q.quote_number}</span>
                    <span className="truncate text-muted-foreground">{q.customer || ""}</span>
                  </span>
                  <span className="whitespace-nowrap text-xs text-muted-foreground">{q.reason}</span>
                </button>
              ))}
            </div>
          )}
        </Panel>
      </div>

      {/* Efficiency footer */}
      <div className="grid grid-cols-2 gap-px bg-border sm:grid-cols-4">
        <Kpi label="Cost / Pound" value={money(data.avg_cost_per_pound)} />
        <Kpi label="Cost / Footprint" value={money(data.avg_cost_per_footprint)} />
        <Kpi label="Override Rate" value={pct(data.override_rate)}
          accent={data.override_rate > 25 ? "text-[hsl(var(--warning))]" : ""} />
        <Kpi label="Overrides" value={num(data.override_count)} />
      </div>
    </div>
  );
}
